/**
 * Sticky buy bar per PDP — sostituisce l'app "Sticky Add to Cart Bar"
 * (Vitals/Appsolve, bundle esterno ~419KB) disinstallata il 13 lug 2026 per
 * l'impatto su LCP. Vanilla JS, zero dipendenze: non duplica la logica di
 * carrello, delega tutto al form/bottone reali della PDP gia' presenti nel
 * tema (stessa AJAX add-to-cart, stesso drawer/notifica).
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.submitButton = document.getElementById(this.dataset.submitButtonId);
    this.quantityInput = document.getElementById(this.dataset.quantityInputId);
    this.qtyValueEl = this.querySelector('[data-qty-value]');
    this.stickyAddButton = this.querySelector('[data-sticky-add-to-cart]');

    if (!this.submitButton) return;

    this.querySelector('[data-qty-minus]').addEventListener('click', () => this.stepQuantity(-1));
    this.querySelector('[data-qty-plus]').addEventListener('click', () => this.stepQuantity(1));
    this.stickyAddButton.addEventListener('click', () => this.addToCart());

    this.mirrorSubmitButtonState();
    this.submitButtonObserver = new MutationObserver(() => this.mirrorSubmitButtonState());
    this.submitButtonObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });

    // Rimuove l'hidden iniziale (serviva solo a evitare un flash prima che
    // il JS fosse pronto): da qui in poi la visibilita' e' gestita dalla
    // classe is-visible, che anima con una transizione CSS invece di
    // comparire/sparire di scatto.
    this.removeAttribute('hidden');

    // Trigger basato sulla posizione di scroll (invece di
    // IntersectionObserver): calcola la posizione assoluta del bottone reale
    // nella pagina una volta, poi confronta con lo scroll corrente. Piu'
    // prevedibile di un observer quando il bottone e' dentro contenitori con
    // comportamenti di scroll/sticky non standard.
    this.updateButtonPosition = () => {
      const rect = this.submitButton.getBoundingClientRect();
      this.buttonBottom = window.scrollY + rect.bottom;
    };
    this.onScroll = () => {
      const shouldShow = window.scrollY > this.buttonBottom;
      this.classList.toggle('is-visible', shouldShow);
      if (shouldShow) this.syncQuantityFromRealInput();
    };

    // Ricalcola la posizione solo se cambia davvero la larghezza, non ad
    // ogni 'resize': su mobile la barra degli indirizzi che si nasconde
    // durante lo scroll scatena resize continui cambiando solo l'altezza,
    // e ricalcolare in quel momento userebbe lo scrollY corrente (non
    // quello iniziale) facendo "inseguire" la soglia allo scroll in corso —
    // bug reale riscontrato il 20 lug 2026 (la barra appariva molto piu'
    // tardi del previsto).
    let lastWidth = window.innerWidth;
    this.onResize = () => {
      if (window.innerWidth !== lastWidth) {
        lastWidth = window.innerWidth;
        this.updateButtonPosition();
      }
    };

    this.updateButtonPosition();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    // Il primo calcolo avviene appena il DOM e' parsato (script defer), ma
    // immagini della galleria e widget di terze parti (recensioni, badge)
    // possono ancora cambiare l'altezza del contenuto sopra il bottone dopo
    // quel momento, spostandolo piu' in basso e rendendo la soglia calcolata
    // troppo bassa (la barra appare "in ritardo" rispetto al bottone reale —
    // bug riscontrato il 20 lug 2026). Ricalcola quando la pagina e'
    // completamente caricata (immagini incluse) e di nuovo poco dopo, per
    // coprire anche i widget che si inizializzano in modo asincrono.
    window.addEventListener('load', this.updateButtonPosition, { once: true });
    setTimeout(this.updateButtonPosition, 1500);
  }

  disconnectedCallback() {
    this.submitButtonObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
  }

  syncQuantityFromRealInput() {
    if (this.quantityInput) {
      this.qtyValueEl.textContent = this.quantityInput.value;
    }
  }

  stepQuantity(direction) {
    const min = this.quantityInput ? Number(this.quantityInput.min) || 1 : 1;
    const max = this.quantityInput?.max ? Number(this.quantityInput.max) : Infinity;
    const step = this.quantityInput ? Number(this.quantityInput.step) || 1 : 1;
    const current = Number(this.qtyValueEl.textContent) || min;
    const next = Math.min(max, Math.max(min, current + direction * step));
    this.qtyValueEl.textContent = next;

    if (this.quantityInput) {
      this.quantityInput.value = next;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  mirrorSubmitButtonState() {
    const isDisabled = this.submitButton.hasAttribute('disabled');
    this.stickyAddButton.disabled = isDisabled;
    const label = this.submitButton.querySelector('span')?.textContent?.trim();
    if (label) this.stickyAddButton.textContent = label;
  }

  addToCart() {
    if (this.quantityInput) {
      this.quantityInput.value = this.qtyValueEl.textContent;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.submitButton.click();
  }
}

customElements.define('sticky-buy-bar', StickyBuyBar);
